import { useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import {
	TIMEOUTS,
	authInactiveIdleCheckLogging,
	authInactiveIdleCheckLoggingFrequent,
} from '../config/timeouts';
import { useAuth } from '../contexts/AuthContext';
import { adaSwal } from '../utils/adaSwal';
import { runSessionExpiredLogout } from '../utils/sessionExpiredFlow';

const logInactiveCheck = authInactiveIdleCheckLogging;

/** I dev: trotle periodiske tick-logger med mindre dette er true (da hver 4s). */
const frequentIdleLog = authInactiveIdleCheckLoggingFrequent;

const logIdle = (label, payload) => {
	if (!logInactiveCheck) {
		return;
	}
	console.log('[auth/idle-check]', new Date().toISOString(), label, payload);
};

/**
 * Varsel før «inaktivitet uten refresh» (12t-vinduet). Access-token fornyes stille i bakgrunnen
 * (axios / useTokenRefresh) — ingen popup der.
 *
 * Logging: `config/timeouts.js` (`AUTH_IDLE_CLIENT_LOG_FREQUENT` / development).
 */
export function useSessionWarning() {
	const {
		isLoggedIn,
		sessionIdleExpiresAt,
		sessionIdleRemainingSeconds,
		sessionIdleSyncedAtClientMs,
		refreshToken,
		checkAuthStatus,
		logout,
	} = useAuth();
	const warnedIdleDeadlineMs = useRef(null);
	/** Én gangs refresh mot server når klient-teller når null (kort test / presis utløp). */
	const idleExpiryProbeSentRef = useRef(false);
	const lastIdleTickLogMsRef = useRef(0);

	/** Varsel før utløp av inaktivitetsøkt (synket sekunder fra server når tilgjengelig). */
	useEffect(() => {
		if (!isLoggedIn || !sessionIdleExpiresAt) {
			warnedIdleDeadlineMs.current = null;
			idleExpiryProbeSentRef.current = false;
			return;
		}

		idleExpiryProbeSentRef.current = false;
		lastIdleTickLogMsRef.current = 0;

		const deadlineMs = new Date(sessionIdleExpiresAt).getTime();
		if (!Number.isFinite(deadlineMs)) {
			logIdle('avbrutt', { grunn: 'ugyldig sessionIdleExpiresAt', sessionIdleExpiresAt });
			return;
		}

		const maxReasonableRemainingSec =
			TIMEOUTS.sessionInactivityTimeoutMinutes * 60 + 120;

		if (
			typeof sessionIdleRemainingSeconds === 'number' &&
			sessionIdleRemainingSeconds > maxReasonableRemainingSec
		) {
			console.warn(
				'[auth/idle-check] Klient-forventet maks ca. ' +
					maxReasonableRemainingSec +
					's inaktivitet, men server rapporterer ' +
					sessionIdleRemainingSeconds +
					's. Sjekk at `SESSION_INACTIVITY_LIMIT_MS` i backend `config/timeouts.js` matcher frontend.'
			);
		}

		const getMsLeft = () => {
			if (
				sessionIdleSyncedAtClientMs != null &&
				typeof sessionIdleRemainingSeconds === 'number'
			) {
				const elapsed = Date.now() - sessionIdleSyncedAtClientMs;
				return Math.max(
					0,
					sessionIdleRemainingSeconds * 1000 - elapsed
				);
			}
			return deadlineMs - Date.now();
		};

		const maybeWarnIdle = async () => {
			const msLeft = getMsLeft();
			const nowMs = Date.now();
			const shouldLogTick =
				frequentIdleLog ||
				msLeft <= 0 ||
				msLeft <= TIMEOUTS.sessionIdleWarningMsBeforeExpiry + 15_000 ||
				nowMs - lastIdleTickLogMsRef.current >= 15_000;
			if (shouldLogTick) {
				lastIdleTickLogMsRef.current = nowMs;
				logIdle('tick', {
					msLeft,
					sekIgjen: Math.round(msLeft / 1000),
					varselTerskelMs: TIMEOUTS.sessionIdleWarningMsBeforeExpiry,
					periodicRefreshAv: TIMEOUTS.tokenRefreshPeriodicDisabled,
					sessionIdleRemainingSeconds,
					sessionIdleSyncedAtClientMs,
					sessionIdleExpiresAt,
				});
			}

			if (msLeft <= 0) {
				// Ikke kall Swal.close() her — det ga et «blink» før neste dialog, og brukeren
				// rakk ikke å lese meldingen. Neste steg (runSessionExpiredLogout) erstatter/lukker trygt.
				if (Swal.isVisible()) {
					logIdle('frist ute med åpent Swal', {
						grunn: 'bevar synlig dialog til utløpsmelding vises',
					});
				}
				logIdle('frist ute (klient)', { msLeft });
				if (!idleExpiryProbeSentRef.current) {
					idleExpiryProbeSentRef.current = true;
					const ok = await refreshToken();
					if (!ok) {
						logIdle('refresh feilet → runSessionExpiredLogout', {});
						await runSessionExpiredLogout(logout);
					} else {
						logIdle('refresh lyktes (frist forlenget)', {});
						idleExpiryProbeSentRef.current = false;
					}
				}
				return;
			}

			if (Swal.isVisible()) {
				logIdle('hopp over', { grunn: 'Swal allerede synlig' });
				return;
			}
			if (msLeft > TIMEOUTS.sessionIdleWarningMsBeforeExpiry) {
				return;
			}
			if (warnedIdleDeadlineMs.current === deadlineMs) {
				logIdle('hopp over varsel', { grunn: 'allerede vist for denne deadlineMs' });
				return;
			}
			warnedIdleDeadlineMs.current = deadlineMs;

			const minLeft = Math.max(1, Math.ceil(msLeft / 60000));
			const result = await adaSwal({
				icon: 'warning',
				title: 'Inaktivitet',
				html: `<p>Uten å fornye økten blir du snart logget ut (om ca. <strong>${minLeft}</strong> min) fordi du har vært inaktiv for lenge.</p><p>Vil du forbli innlogget?</p>`,
				showCancelButton: true,
				confirmButtonText: 'Forbli innlogget',
				cancelButtonText: 'Senere',
				allowOutsideClick: true,
			});

			if (result.isConfirmed) {
				const ok = await refreshToken();
				if (ok) {
					await checkAuthStatus();
					warnedIdleDeadlineMs.current = null;
				} else {
					await runSessionExpiredLogout(logout);
				}
			}
		};

		const id = setInterval(maybeWarnIdle, TIMEOUTS.sessionWarningTickMs);
		maybeWarnIdle();

		const msUntilZero = getMsLeft();
		const hardDelay =
			msUntilZero > 0
				? Math.min(msUntilZero + 250, 2147483647)
				: 0;
		const hardTimer = setTimeout(() => {
			maybeWarnIdle();
		}, hardDelay);

		return () => {
			clearInterval(id);
			clearTimeout(hardTimer);
		};
	}, [
		isLoggedIn,
		sessionIdleExpiresAt,
		sessionIdleRemainingSeconds,
		sessionIdleSyncedAtClientMs,
		refreshToken,
		checkAuthStatus,
		logout,
	]);
}
